import { InsightState } from '@core/insights';
import { css } from '@emotion/react';
import { SvgSunAndClouds } from '@web/components/svgs/insights-icons';
import {
  Ph10,
  SHWoman,
  SvgWellnessHeart,
} from '@web/components/svgs/wellness-icons';
import { useWindowSize } from '@web/components/use-window-size';
import { theme, variants } from '@web/features/theme';
import { getDay } from 'date-fns';
import * as React from 'react';
import {
  DayColors,
  dayColors,
  DayOfActivity,
  DaysOfTheWeek,
  daysOfTheWeek,
  DaysOfTheWeekRotations,
  filterByTimeframe,
  getDayLines,
  getWeekLines,
  groupActivitiesByDate,
  mapActivitiesToPoints,
  PointColors,
  PointConfig,
  POINT_BLUR,
  POINT_HEIGHT,
  POINT_SHADOW,
  POINT_SIZE,
  POINT_WIDTH,
  SIZE,
} from './spiral-utils';

/**
 * # Insights Spiral
 * @packageDescription
 *
 *  * So this page is responsible for creating a spiral on the insights page.
 * The spiral was a bit complicated to implement so I'm sure it'll
 *  take even longer to understand
 *
 * Here are the steps required to build the spiral.
 *
 * Each day of the week represents a line on the spiral.
 * 360 / 7 = 51.43 So every ~51deg we're creating a point with a line to the center
 *
 * Using these lines of the week, for every day activity
 *  - Use the index of the point in the array to calculate the distance from the center.
 *  - Use the activity to calculate the color of the point.
 *  - Draw a point on one of these lines (depending on the day of the week)
 */

export function InsightsSpiral({
  timeframe,
  type,
  date,
  wellnessChecks,
}: InsightState) {
  const { width } = useWindowSize();

  const [isHelpOpen, setIsHelpOpen] = React.useState(false);
  const toggleHelp = () => setIsHelpOpen((prev) => !prev);

  const filteredActivities = filterByTimeframe(timeframe, date, wellnessChecks);

  const isEmpty = filteredActivities.length <= 0;

  const groupedActivitiesByDate = groupActivitiesByDate(
    filteredActivities,
    date,
  );

  const size = Math.min((width ?? 0) * 0.85, SIZE); // 0, 85% of the width of the screen, or the max size
  const radius = size - 50;
  const config: PointConfig = {
    distance: size / 40,
    size: size / 175,
    shadow: 0,
    blur: 0,
    height: 2,
    width: 1,
  };

  const dayLines = getDayLines({ size, radius });
  const points = mapActivitiesToPoints(
    groupedActivitiesByDate,
    type,
    config,
    dayLines,
  );
  const weekLines = getWeekLines(points);

  const today = daysOfTheWeek.find(
    (day: DaysOfTheWeek) =>
      date && day === daysOfTheWeek[getDay(new Date(date))],
  );

  const rotation = today && DaysOfTheWeekRotations[today];

  if (size === 0) {
    return null;
  }

  if (isEmpty) {
    return (
      <div css={styles.wrapper} className="empty">
        {emptyConfig[type].icon}

        <p>
          To see these insights you must complete at least one{' '}
          {emptyConfig[type].text}.
        </p>
      </div>
    );
  }

  return (
    <div css={styles.wrapper}>
      <button type="button" css={styles.help} onClick={toggleHelp}>
        ?
      </button>

      {isHelpOpen ? (
        <div
          css={{
            padding: variants.space['8'],
            h2: {
              marginBottom: variants.space['4'],
            },

            p: {
              lineHeight: theme.lineHeights.tall,
              marginBottom: variants.space['4'],
            },
          }}
        >
          <h2 css={{ fontWeight: 'bold' }}>How to read your insights</h2>

          <p>
            Each insight tab takes your daily ratings from the check-ins and
            separates theme according to each check-in type.
          </p>

          <p>
            For each day you check in, a node is placed on the outermost ring of
            the time vortex with a color matched to each day's check-in (green =
            overall higher, red = overall lower).
          </p>

          <p>
            If you don't check in, no node is placed in the vortex. If there are
            more than 30 nodes, the oldest node is swallowed by into the time
            vortex.
          </p>

          <p>
            That way, you can see the last month of check-ins and look at the
            daily and weekly patterns over time.
          </p>
          <p>
            After 28 days, the oldest seed is removed from the calendar or
            swallowed into the time vortex.
          </p>
        </div>
      ) : (
        <svg
          className="rr-block"
          xmlns="http://www.w3.org/2000/svg"
          width={size}
          height={size}
          viewBox={`0 0 ${size * 2} ${size * 2}`}
          style={{ transform: rotation ? `rotate(${rotation}deg)` : undefined }}
        >
          {weekLines.reverse().map((line) => (
            <path
              key={line.point1.x + line.point2.y}
              fill={line.weekColor}
              d={`
          M ${line.point1.x} ${line.point1.y}
          C ${line.point1.x} ${line.point1.y} ${line.curveControl.x} ${line.curveControl.y} ${line.point2.x} ${line.point2.y}
          L ${line.point2.x} ${line.point2.y}
          L ${line.point3.x} ${line.point3.y}
          L ${line.point4.x} ${line.point4.y}
          `}
            />
          ))}

          {dayLines.map((line) => (
            <>
              <Line
                key={line.day}
                {...line}
                width={4}
                color={line.day === today ? DayColors.TODAY : line.color}
              />
              <Text
                key={line.point1.x + line.day}
                anchor="middle"
                x={line.point1.x}
                y={line.point1.y}
                color={dayColors[6]}
                text={line.day}
                rotation={rotation}
              />
            </>
          ))}

          {points.map(
            (act) =>
              typeof act !== 'undefined' && (
                <Ellipses key={act.x + act.y} {...act} />
              ),
          )}
        </svg>
      )}
    </div>
  );
}

const emptyConfig = {
  hope: {
    icon: <SvgWellnessHeart />,
    text: 'hope metric when using the time machine',
  },
  physical: {
    icon: <Ph10 />,
    text: 'physical health wellness check after using the time machine',
  },
  spiritual: {
    icon: (
      <div
        css={{
          background:
            'radial-gradient(circle at center,  #8829E6, #A829E6, transparent 75% )',
        }}
      >
        <SHWoman />
      </div>
    ),
    text: 'spiritual health wellness check after using the time machine',
  },
  emotional: {
    icon: <SvgSunAndClouds />,
    text: 'emotional health wellness check after using the time machine',
  },
};

const Ellipses = ({
  x,
  y,
  color = PointColors.TRANSPARENT,
  size = POINT_SIZE,
  blur = POINT_BLUR,
  shadow = POINT_SHADOW,
  rotation = 0,
}: DayOfActivity) => (
  <ellipse
    cx={x}
    cy={y}
    rx={size / POINT_WIDTH}
    ry={size / POINT_HEIGHT}
    fill={color}
    filter={`drop-shadow( 0 0 ${shadow}px ${color}) blur(${blur}px)`}
    transform={`rotate(${rotation}, ${x}, ${y})`}
  />
);

const Text = ({
  x,
  y,
  text,
  color,
  size = SIZE / 12,
  anchor = 'middle',
  rotation = 0,
  ...props
}: {
  x: number;
  y: number;
  text: string | number;
  color: string;
  size?: number;
  anchor?: 'left' | 'middle' | 'right';
  rotation?: number;
}) => (
  <text
    fontSize={`${size}px`}
    fill={color}
    textAnchor={anchor}
    transform={`translate(${x} ${y}) rotate(${-rotation})`}
    {...props}
  >
    <tspan>{text}</tspan>
  </text>
);

const Line = ({
  point1,
  point2,
  color = PointColors.COLOR_50,
  width = 8,
  withBlur = false,
  withOpacity = false,
}: {
  point1: {
    x: number;
    y: number;
  };
  point2: {
    x: number;
    y: number;
  };
  color?: string;
  width?: number;
  withBlur?: boolean;
  withOpacity?: boolean;
}) => {
  return (
    <line
      x1={point1.x}
      y1={point1.y}
      x2={point2.x}
      y2={point2.y}
      stroke={color}
      strokeWidth={width}
      filter={withBlur ? `blur(${2}px)` : ''}
      opacity={withOpacity ? 0.5 : 1}
      strokeLinecap="round"
    />
  );
};

const styles = {
  wrapper: css`
    display: block;
    position: relative;
    margin: ${theme.space[2]} auto ${theme.space[4]};
    border: ${theme.borders['1px']} ${theme.colors.whiteAlpha[900]};
    border-radius: ${theme.radii.lg};

    &.empty {
      display: flex;
      flex-direction: column;
      justify-content: center;
      align-items: center;
      padding: ${theme.space[12]};
      text-align: center;

      p {
        margin-top: ${theme.space[12]};
      }
    }

    &:before {
      content: '';
      position: absolute;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      height: 100%;
      width: 100%;
      background-color: ${theme.colors.blackAlpha[700]};
      filter: blur(25px);
      z-index: -1;

      @supports (-webkit-backdrop-filter: none) or (backdrop-filter: none) {
        background-color: ${theme.colors.blackAlpha[400]};
        backdrop-filter: blur(25px);
        opacity: 0.9;
        filter: blur(0px);
      }
    }
  `,

  help: css`
    cursor: pointer;
    z-index: ${theme.zIndices.overlay}+7;
    position: absolute;
    display: block;
    top: 10px;
    right: 10px;
    background: ${theme.colors.whiteAlpha[900]};
    border: none;
    height: ${theme.space[5]};
    width: ${theme.space[5]};
    border-radius: ${theme.radii.full};
  `,
};
